import DOMPurify from "isomorphic-dompurify";

import { ERROR_CODES } from "@/lib/errors/codes";
import { ErrorFactory } from "@/lib/errors/factory";

async function sanitizeSVGFile(file: File) {
  try {
    if (!file.type.includes("svg") && !file.name.toLowerCase().endsWith(".svg")) {
      throw new Error("File is not an SVG");
    }

    const svgContent = await file.text();

    const sanitized = DOMPurify.sanitize(svgContent, {
      USE_PROFILES: { svg: true, svgFilters: true },
      ALLOWED_TAGS: [
        "svg",
        "g",
        "defs",
        "clipPath",
        "mask",
        "pattern",
        "image",
        "switch",
        "marker",
        "symbol",
        "use",
        "style",
        "title",
        "desc",
        "metadata",
        "linearGradient",
        "radialGradient",
        "stop",
        "animate",
        "animateTransform",
        "animateMotion",
        "circle",
        "ellipse",
        "line",
        "rect",
        "polyline",
        "polygon",
        "path",
        "text",
        "tspan",
        "tref",
        "textPath",
        "filter",
        "feGaussianBlur",
        "feColorMatrix",
        "feOffset",
        "feMerge",
        "feMergeNode",
        "feFlood",
      ],
      FORBID_TAGS: [
        "script",
        "object",
        "embed",
        "iframe",
        "form",
        "input",
        "textarea",
        "button",
        "select",
        "option",
        "link",
        "meta",
        "base",
        "frame",
        "frameset",
        "applet",
        "audio",
        "video",
        "source",
        "track",
      ],
      FORBID_ATTR: [
        "onload",
        "onerror",
        "onclick",
        "onmouseover",
        "onmouseout",
        "onmousedown",
        "onmouseup",
        "onkeydown",
        "onkeyup",
        "onkeypress",
        "onfocus",
        "onblur",
        "onchange",
        "onsubmit",
        "onreset",
        "onselect",
        "onabort",
        "oncanplay",
        "oncanplaythrough",
        "ondurationchange",
        "onemptied",
        "onended",
        "oninput",
        "oninvalid",
        "onloadeddata",
        "onloadedmetadata",
        "onloadstart",
        "onpause",
        "onplay",
        "onplaying",
        "onprogress",
        "onratechange",
        "onseeked",
        "onseeking",
        "onstalled",
        "onsuspend",
        "ontimeupdate",
        "onvolumechange",
        "onwaiting",
        "onwheel",
        "onscroll",
        "onresize",
        "onhashchange",
        "onpopstate",
        "formaction",
        "formenctype",
        "formmethod",
        "formnovalidate",
        "formtarget",
      ],
      ALLOW_DATA_ATTR: false,
      KEEP_CONTENT: false,
      RETURN_DOM: false,
      RETURN_DOM_FRAGMENT: false,
      SANITIZE_DOM: true,
      WHOLE_DOCUMENT: false,
      FORCE_BODY: false,
    });

    if (!sanitized.includes("<svg") || !sanitized.includes("</svg>")) {
      throw new Error("SVG structure was corrupted during sanitization");
    }

    const sanitizedFile = new File([sanitized], file.name, { type: "image/svg+xml" });
    return sanitizedFile;
  } catch (error) {
    return null;
  }
}

async function sanitizeIfSVG(file: File | Blob) {
  if (file instanceof File) {
    if (file.type.includes("svg") || file.name.toLowerCase().endsWith(".svg")) {
      const sanitized = await sanitizeSVGFile(file);

      if (sanitized) {
        return sanitized;
      } else {
        throw ErrorFactory(ERROR_CODES.SVG_SANITIZATION_FAILED, {
          error: new Error("SVG sanitization failed"),
          metadata: {
            fileName: file.name,
            fileType: file.type,
          },
        });
      }
    }
  }

  return file;
}

export { sanitizeIfSVG, sanitizeSVGFile };
